/**
 * Slack inbound governance config, built from the per-agent env contract.
 *
 * ADR-0047: the opencode runtime's manager-side SenderGate must build the exact
 * same `SenderPolicy` + `SlackPeerClassifierConfig` the Claude Code slack-channel
 * MCP builds, from the exact same env vars (Brad's option (a): the manager
 * materializes the identical env contract at spawn). This module is the shared,
 * pure home for that env -> config parsing so the two runtimes cannot diverge on
 * correctness-critical gating (a wrong policy silently drops production traffic).
 *
 * Faithful port of the two IIFEs in `packages/mcp/src/slack-channel.ts`
 * (`SLACK_SENDER_POLICY` ~L303 and `SLACK_PEER_DISABLED_MODE` ~L635 +
 * `SLACK_PEER_CLASSIFIER_CONFIG` ~L697), parameterized on an injected env map +
 * the agent's team id, with `process.stderr.write` replaced by an injected
 * `onWarn` sink so core stays I/O-agnostic. Fail-closed behaviour (throw on
 * missing required config, bias to the safe value on a malformed enum) is
 * preserved exactly.
 *
 * NOTE (dedup follow-up): slack-channel.ts still has its own inline copies of
 * this parsing. Until it is refactored to import these builders, keep the two in
 * sync - drift only mis-gates opencode agents (not the live Claude fleet), but it
 * is still a latent traffic-drop bug. Refactoring the MCP onto these builders is
 * the single-source-of-truth end state.
 */

import type { SenderPolicy } from './slack-inbound-filter.js';
import {
  parsePeerAgentModeEnv,
  parsePeerGroupIdsEnv,
  parsePeersEnv,
  parseThirdPartyBotsEnv,
  type SlackPeerClassifierConfig,
} from './slack-peer-classifier.js';

/** A read-only view of the process env (or any equivalent map). */
export type EnvLike = Record<string, string | undefined>;

export interface SlackSenderPolicyContext {
  /**
   * The agent's team id (AGT_TEAM_ID). Required for the team-scoped modes
   * (`team_only` / `team_agents_only` / `manager_only`); a missing value there
   * fails closed by throwing rather than silently dropping traffic.
   */
  agentTeamId: string | null;
}

/**
 * Build the Slack `SenderPolicy` from the per-agent env contract.
 *
 * Throws on a misconfiguration that would otherwise silently drop production
 * traffic (e.g. `team_agents_only` with no team id, `internal_only` with no home
 * workspace id) - the caller should surface this as a loud gate-setup failure,
 * exactly as the Claude MCP fails its boot.
 */
export function buildSlackSenderPolicyFromEnv(
  env: EnvLike,
  ctx: SlackSenderPolicyContext,
): SenderPolicy {
  const raw = (env['SLACK_SENDER_POLICY'] ?? 'all').trim().toLowerCase();
  // internal_only is an orthogonal axis layered on top of mode (ENG-5843).
  const internalOnly = env['SLACK_INTERNAL_ONLY'] === 'true';
  const homeTeamId = env['SLACK_HOME_TEAM_ID'];
  if (internalOnly && !homeTeamId) {
    throw new Error(
      'SLACK_INTERNAL_ONLY=true requires SLACK_HOME_TEAM_ID. ' +
        'The bot install must have a known team_id (run auth.test against the workspace and persist the result).',
    );
  }
  const internalOnlyFields = internalOnly
    ? ({ internalOnly: true as const, homeTeamId: homeTeamId! })
    : ({});

  // ENG-7786: bind label admission to a registered-sender identity (the label
  // alone is spoofable). The registry is the UNION of two sources, both keyed
  // on each agent's Slack `evt.user` (bot_user_id):
  //   1. SLACK_PEERS          - the operator-curated peer list (may include
  //                             cross-team peers reached via a grant).
  //   2. SLACK_TEAM_PEER_USER_IDS - the COMPLETE same-team roster the host
  //                             already builds (every active same-team Slack
  //                             agent).
  //
  // ENG-7786-followup: source 2 is essential. The restrictive modes
  // (team_agents_only / team_only / manager_only path 2) are documented to
  // admit ANY same-team labelled agent, gated only by the team_id check below.
  // Keying the registry on the curated SLACK_PEERS subset alone dropped every
  // same-team agent an operator had not hand-picked - and the moment ONE peer
  // was curated the registry flipped from undefined (trust) to a one-element
  // set, silently dropping the rest of the team. Unioning in the full roster
  // restores the documented admit set while keeping the anti-spoof binding.
  // The team_id check still runs afterwards, so widening the roster to
  // cross-team curated peers cannot leak cross-team traffic into a team-scoped
  // mode.
  //
  // Present-only sentinel: when NEITHER source is set (a solo agent with no
  // same-team peers, or an older manager pre-wiring) the field stays undefined
  // and the label is trusted on content alone - today's behaviour, no change.
  // `mode: 'all'` never reaches the label check regardless.
  const slackPeersRaw = env['SLACK_PEERS'];
  const teamPeerUserIdsRaw = env['SLACK_TEAM_PEER_USER_IDS'];
  const registeredAgentSlackUserIds = new Set<string>();
  if (slackPeersRaw && slackPeersRaw.trim().length > 0) {
    for (const p of parsePeersEnv(slackPeersRaw, env['SLACK_PEERS_GATE'])) {
      registeredAgentSlackUserIds.add(p.bot_user_id);
    }
  }
  if (teamPeerUserIdsRaw && teamPeerUserIdsRaw.trim().length > 0) {
    for (const id of teamPeerUserIdsRaw.split(',')) {
      const trimmed = id.trim();
      if (trimmed.length > 0) registeredAgentSlackUserIds.add(trimmed);
    }
  }
  const registryFields =
    registeredAgentSlackUserIds.size > 0 ? { registeredAgentSlackUserIds } : {};

  const teamId = ctx.agentTeamId;

  if (raw === 'all') return { mode: 'all', ...internalOnlyFields, ...registryFields };
  if (raw === 'agents_only') return { mode: 'agents_only', ...internalOnlyFields, ...registryFields };
  if (raw === 'team_agents_only') {
    if (!teamId) {
      throw new Error('SLACK_SENDER_POLICY=team_agents_only requires AGT_TEAM_ID');
    }
    return { mode: 'team_agents_only', teamId, ...internalOnlyFields, ...registryFields };
  }
  if (raw === 'team_only') {
    if (!teamId) {
      throw new Error('SLACK_SENDER_POLICY=team_only requires AGT_TEAM_ID');
    }
    const teamPrincipalSlackUserIds = (env['SLACK_SENDER_POLICY_TEAM_PRINCIPAL_IDS'] ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    return { mode: 'team_only', teamId, teamPrincipalSlackUserIds, ...internalOnlyFields, ...registryFields };
  }
  if (raw === 'manager_only') {
    const principalSlackUserId = env['SLACK_SENDER_POLICY_PRINCIPAL_ID'];
    if (!teamId) {
      throw new Error('SLACK_SENDER_POLICY=manager_only requires AGT_TEAM_ID');
    }
    if (!principalSlackUserId) {
      throw new Error(
        'SLACK_SENDER_POLICY=manager_only requires SLACK_SENDER_POLICY_PRINCIPAL_ID. ' +
          'Set agent.reports_to_person with a slack_user_id in contact_preferences and re-run /host/refresh.',
      );
    }
    return { mode: 'manager_only', teamId, principalSlackUserId, ...internalOnlyFields, ...registryFields };
  }
  throw new Error(`Invalid SLACK_SENDER_POLICY=${JSON.stringify(env['SLACK_SENDER_POLICY'])}`);
}

export type SlackPeerDisabledMode = 'off' | 'cross_team_only' | 'all';

/**
 * Parse the `PEER_DISABLED` kill-switch. Fail-closed: any non-empty
 * unrecognised value biases to `'all'` (every peer surface short-circuited)
 * rather than falling through to `'off'` and silently re-enabling peer traffic.
 */
export function parseSlackPeerDisabledModeEnv(
  env: EnvLike,
  onWarn?: (msg: string) => void,
): SlackPeerDisabledMode {
  const raw = (env['PEER_DISABLED'] ?? '').trim().toLowerCase();
  if (raw === '' || raw === 'off') return 'off';
  if (raw === 'cross_team_only' || raw === 'all') return raw;
  onWarn?.(
    `invalid PEER_DISABLED=${JSON.stringify(env['PEER_DISABLED'])} - defaulting to 'all' (fail-closed)`,
  );
  return 'all';
}

/**
 * Build the Slack peer-agent classifier config from the per-agent env contract.
 * Thin composition over the pure `parse*` primitives that ship with the peer
 * classifier, plus the `PEER_DISABLED` kill-switch.
 */
export function buildSlackPeerClassifierConfigFromEnv(
  env: EnvLike,
  opts?: { onWarn?: (msg: string) => void },
): SlackPeerClassifierConfig {
  return {
    peer_agent_mode: parsePeerAgentModeEnv(env['SLACK_PEER_AGENT_MODE']),
    peer_group_ids: parsePeerGroupIdsEnv(env['SLACK_PEER_GROUP_IDS']),
    peers: parsePeersEnv(env['SLACK_PEERS'], env['SLACK_PEERS_GATE']),
    peer_disabled_mode: parseSlackPeerDisabledModeEnv(env, opts?.onWarn),
    // ENG-8669 follow-up: this builder never populated the third-party
    // allowlist. ENG-8669 read `SLACK_THIRD_PARTY_BOTS` only in the MCP
    // adapter's own inline config, so every consumer of THIS builder — the
    // opencode manager-side SenderGate among them — saw `third_party_bots`
    // as undefined and could not admit a third-party bot under any
    // configuration. The allowlist was wired into one of the two Slack ingress
    // paths. Reading it here makes the two agree.
    third_party_bots: parseThirdPartyBotsEnv(env['SLACK_THIRD_PARTY_BOTS']),
  };
}
